import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useLang } from "@/context";
import { MaskedLine } from "@/components/Reveal";

const HERO_IMG = "/photos/merce-face-bw.png";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const { t } = useLang();
  const navigate = useNavigate();
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [2.5, -2.5]), { stiffness: 60, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-2.5, 2.5]), { stiffness: 60, damping: 20 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-screen flex items-start lg:items-center overflow-hidden pt-28 pb-24 lg:pb-16"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 top-1/2 -translate-y-1/2 font-serif text-[38vw] leading-none text-charcoal/[0.035] select-none hidden lg:block"
      >
        MH
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 w-full grid grid-cols-12 gap-y-10 lg:gap-6 items-center">
        <motion.div className="col-span-12 lg:col-span-7 relative z-10 min-w-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="gold-rule w-14" aria-hidden="true" />
            <span data-testid="hero-eyebrow" className="text-[11px] sm:text-xs uppercase tracking-[0.34em] text-gold">
              {t.hero.eyebrow}
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-serif text-[12vw] sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[1.04] text-charcoal font-medium"
          >
            {t.hero.lines.map((line, i) => (
              <MaskedLine key={line} delay={0.35 + i * 0.18} inView={false}>
                {line}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-8 max-w-xl text-base sm:text-lg text-ink/80 leading-relaxed font-light"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            data-testid="hero-trust"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-8 border-l border-gold/60 pl-5 max-w-xl"
          >
            <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-gold">
              {t.hero.trust}
            </p>
            <p className="mt-2.5 text-sm text-ink/70 leading-relaxed font-light">{t.hero.trustSub}</p>
            <p className="mt-3 text-xs text-taupe leading-relaxed font-light">{t.hero.trustCred}</p>
          </motion.div>

          <motion.div
            data-testid="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] sm:text-xs uppercase tracking-[0.22em] text-taupe"
          >
            {t.hero.stats.map((s, i) => (
              <span key={s} className="flex items-center gap-3">
                {i > 0 && <span className="w-1 h-1 bg-gold rotate-45 inline-block" aria-hidden="true" />}
                {s}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => navigate("/contact")}
              className="bg-charcoal text-ivory border border-charcoal px-9 py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-gold hover:border-gold hover:text-charcoal transition-colors duration-300"
            >
              {t.hero.ctaPrimary}
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => navigate("/experience")}
              className="border border-charcoal/60 text-charcoal px-9 py-4 text-[11px] uppercase tracking-[0.24em] hover:border-gold hover:text-gold transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </motion.div>

        <div className="col-span-12 lg:col-span-5 relative min-w-0" style={{ perspective: 1200 }}>
          <motion.div
            data-testid="hero-portrait"
            onMouseMove={onMouseMove}
            onMouseLeave={() => {
              mx.set(0.5);
              my.set(0.5);
            }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative mx-auto max-w-md lg:max-w-none"
          >
            <div className="absolute -top-5 -left-5 w-24 h-24 border-t border-l border-gold/60" aria-hidden="true" />
            <div className="absolute -bottom-5 -right-5 w-24 h-24 border-b border-r border-gold/60" aria-hidden="true" />

            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
              className="overflow-hidden"
            >
              <motion.img
                src={HERO_IMG}
                alt={t.hero.portraitAlt}
                style={{ y: imgY }}
                initial={{ scale: 1.15 }}
                animate={{ scale: 1.06 }}
                transition={{ duration: 1.8, delay: 0.5, ease: EASE }}
                className="w-full aspect-[4/5] object-cover will-change-transform"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
